<script setup>
import { datadogLogs } from "@datadog/browser-logs";
import pLimit from "p-limit";
import { computed, defineEmits, defineProps, provide, reactive, readonly, ref, toRefs, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import EventsCard from "@/components/ExperiencePage/ProgramDetailsSection/excursions/Components/EventsCards";
import CuratedExperiencesSection from "@/components/ExperiencePage/ProgramDetailsSection/excursions/CuratedExperiences/CuratedExperiencesSection.vue";
import ApplyButton from "@/components/shared/Button/ApplyButton.vue";
import { showNewExcursionConfiguratorDesign } from "@/composables/authorization.js";
import { ERROR_TIMEOUT } from "@/constants.js";
import eventsService from "@/services/events.js";
import programSessions from "@/services/programSessions.js";
import {
  deriveExcursionRules,
  filterExcursionsByRules,
  isExcursionIncluded as isExcursionIncludedByRules,
} from "@/util/excursionRules";
import { pastSessionDeadline } from "@/util/pastSessionDeadline";
import { specialPricingOverrideParam } from "@/util/specialPricingOverride";
import LoadingText from "../team/components/LoadingText";

defineOptions({
  name: "ExcursionBlock",
});

const toast = useToast();
const store = useStore();
const emit = defineEmits(["personalizeAndApply", "observeSection"]);

const props = defineProps({
  sessionId: {
    type: String,
    required: true,
  },
  externalLink: {
    type: String,
    default: "",
  },
  registerType: {
    type: String,
    default: "Personalize & Apply",
  },
});

const limit = pLimit(2);

const state = reactive({
  loading: false,
  excursions: [],
});

const { sessionId } = toRefs(props);
const sessions = ref(undefined);

const homeInstitutionId = computed(() => store.state.profileData?.colleges?.[0]?.college_id ?? "");

const loadProgramSession = async (programSessionId) => {
  if (programSessionId) {
    return programSessions
      .getProgramSession({
        id: programSessionId,
        rules: true,
        home_institution_id: homeInstitutionId.value,
        ...specialPricingOverrideParam(),
      })
      .then((programSesssion) => {
        sessions.value = programSesssion?.data?.data?.items ?? undefined;
      });
  } else {
    sessions.value = undefined;
    return;
  }
};

watch(sessionId, () => {
  loadProgramSession(sessionId.value);
});

const sessionEvents = computed(() => state.excursions);
const goToPersonalizeAndApply = () => {
  emit("personalizeAndApply", sessionId.value);
};

const loadExcursions = async () => {
  state.excursions = [];
  state.loading = true;

  if (sessions.value?.session_events) {
    showSection.value = true;
    const occurrenceRequests = sessions.value?.session_events.map((excursion) =>
      limit(() =>
        eventsService.getOccurrencesAnonymous({ occurrence_ids: excursion.event_id }).catch((error) => {
          toast.open({
            message: "Failed to load events & excursions.",
            type: "error",
            position: "bottom",
            duration: ERROR_TIMEOUT,
          });
          datadogLogs.logger.error(error.message ?? "Error loading occurrences data", { event: excursion.event_id });
          return null;
        }),
      ),
    );
    const occurrenceResponse = await Promise.all(occurrenceRequests);
    const mappedOccurrences = occurrenceResponse.map((response) => {
      const resp = response?.data?.data?.items?.[0]?.associated_event ?? {};
      resp.id = response?.data?.data?.items?.[0]?.id;
      resp.event_id = response?.data?.data?.items?.[0]?.event_id;
      resp.occurrence_price_student = response?.data?.data?.items?.[0]?.price_student;
      resp.start_date_time = response?.data?.data?.items?.[0]?.start_date_time;
      resp.end_date_time = response?.data?.data?.items?.[0]?.end_date_time;
      resp.start_date_time_is_final = response?.data?.data?.items?.[0]?.start_date_time_is_final;
      resp.end_date_time_is_final = response?.data?.data?.items?.[0]?.end_date_time_is_final;
      return resp;
    });
    // Filter to excursions named by a selection rule before categorizing, so this page shares
    // one visibility gate with the registration screens (AP-5593 / AP-5181).
    const occurrences = filterExcursionsByRules(mappedOccurrences, eventRules);

    if (occurrences.length) {
      const eventRequests = occurrences.map((occurrence) =>
        limit(() =>
          eventsService.getOneAnonymous(occurrence.event_id).catch((error) => {
            toast.open({
              message: "Failed to load events & excursions.",
              type: "error",
              position: "bottom",
              duration: ERROR_TIMEOUT,
            });
            datadogLogs.logger.error(error.message ?? "Error loading occurrences data", {
              event: occurrence.event_id,
            });
          }),
        ),
      );
      const eventResponse = await Promise.all(eventRequests);
      const events = eventResponse
        .map((response) => response?.data?.data)
        .filter((item) => item)
        .reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {});

      state.excursions = occurrences.map((occurrence) => ({
        ...occurrence,
        event: events[occurrence.event_id],
      }));
    }

    state.loading = false;
  } else {
    state.excursions = [];
    state.loading = false;
  }
};

//  PRICE IS INCLUDED? SECTION
const eventRules = reactive({
  andIds: [],
  orIds: [],
  defaultIds: [],
});

const loadRules = () => {
  const { andIds, orIds, defaultIds } = deriveExcursionRules(sessions.value?.session_event_selection_rules ?? []);
  eventRules.andIds = andIds;
  eventRules.orIds = orIds;
  eventRules.defaultIds = defaultIds;
};

const defaultEvent = computed(() => {
  // MVP: Only one event is default
  const defaultEventId = sessions.value?.session_event_selection_rules?.[0]?.default_event_id;

  if ([null, undefined, "undefined"].includes(defaultEventId)) return {};
  const foundExcursion = state.excursions.find((excursion) => excursion.id === defaultEventId);
  return foundExcursion ? foundExcursion.event : {};
  // Post-MVP: There could be multiple default events
});

provide("eventRules", readonly(eventRules));

// AP-5593: new curated-experiences design (behind the show-new-excursion-configurator-design flag)
const isExcursionIncluded = (excursion) => isExcursionIncludedByRules(eventRules, excursion.id);

const includedExcursions = computed(() => state.excursions.filter((excursion) => isExcursionIncluded(excursion)));

const optionalExcursions = computed(() => state.excursions.filter((excursion) => !isExcursionIncluded(excursion)));

const priceFlagEnabled = computed(() => Boolean(store.state.featureFlags?.["program-page-card-price"]));

const showSection = ref(sessions.value?.session_events.length ? true : false);

watch(
  sessions,
  () => {
    state.loading = true;
    loadExcursions();
    loadRules();
  },
  {
    immediate: true,
    deep: true,
  },
);

watch([() => showSection.value, () => state.excursions], ([newShowSection, newExcursions]) => {
  if (newShowSection && newExcursions.length) emit("observeSection", "excursions");
});
</script>

<template>
  <div v-if="showSection && state.excursions.length" class="pt-20 px-4 md:px-8 text-indigo-base">
    <div class="grid lg:grid-cols-2 grid-flow-row gap-4">
      <h2 class="self-center uppercase font-bold text-4xl">
        {{ showNewExcursionConfiguratorDesign ? "Curated Experiences" : "Excursions & Activities" }}
      </h2>
    </div>
    <div class="mt-4 mb-2 text-lg">
      <p>
        {{
          showNewExcursionConfiguratorDesign
            ? "Go beyond the classroom with curated experiences that offer new opportunities to explore and gain new skills throughout your program."
            : "Go beyond the classroom with excursions and events that offer new experiences and insights throughout your program."
        }}
      </p>
    </div>
    <LoadingText v-if="state.loading || !sessionEvents?.length" class="mt-4" />
    <CuratedExperiencesSection
      v-if="showNewExcursionConfiguratorDesign"
      :included-excursions="includedExcursions"
      :optional-excursions="optionalExcursions"
      :show-prices="priceFlagEnabled"
    />
    <EventsCard v-else :events="sessionEvents" :default-event="defaultEvent" />

    <!-- The curated-experiences panel ends flush with its own padding, leaving the CTA sitting on
         top of it. `mt-20` is the same gap AcademicSection puts between its content and its CTA.
         Gated on the flag so the flag-off page keeps its current spacing. -->
    <div
      class="md:h-40 flex justify-end"
      :class="showNewExcursionConfiguratorDesign ? 'mt-20' : ''"
    >
      <div class="w-full lg:w-96 grid md:grid-rows-2 grid-flow-rows gap-2 md:gap-4">
        <div class="hidden sm:block">
          <ApplyButton
            :id="sessions?.id"
            :disabled="pastSessionDeadline(sessions)"
            type="button"
            show-arrow
            :outlined="true"
            :external-link="props.externalLink"
            :register-type="props.registerType"
            class="py-2 mt-3 xs:mt-0 text-sm"
            event-category="Program Page Personalize CTA"
            @click="goToPersonalizeAndApply"
          />
        </div>
      </div>
    </div>
    <hr class="my-1 border-b-3 md:mt-20 border-gray-200" />
  </div>
</template>
